import * as React from 'react'
import * as z from 'zod'
import * as Dialog from '~/sotano-kit/primitives/dialog'

import { useToggleState } from '~/hooks/use-toggle-state'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/sotano-kit/primitives/tabs'
import { run } from '@basehub/utils'
import { basehub } from 'basehub'
import { IntegrationInstructionsItem } from '../../../../../../../../../../../basehub-types'
import { env } from '~/env'
import { BreadcrumbBlock } from '.'
import { $breadcrumbCta } from './preview-button'
import {
  CodeSnippet,
  CodeSnippetProps,
} from '~/sotano-kit/primitives/code-snippet'
import { terminalTheme } from '~/sotano-kit/primitives/bshb-theme'
import { Icon } from 'basehub/react-icon'
import { RichText } from 'basehub/react-rich-text'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useIsExternalVisitor } from '~/hooks-data/use-user-role'
import { Environment, getURL } from '@basehub/shared-constants'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { isUserRepo } from '@basehub/data/replicache/mutators/_public-types'
import { isBranch } from '@basehub/data/replicache/index'
import { BaseHubImage } from 'basehub/next-image'
import { ExternalLinkIcon } from '@radix-ui/react-icons'
import { slugify } from '@basehub/utils/slugify'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { clsx } from 'clsx'

export const ConnectAIAgentInstructionsModal = ({
  block,
}: {
  block: BreadcrumbBlock
}) => {
  const { isOn, handleToggle, handleOff } = useToggleState()
  const isVisitor = useIsExternalVisitor()
  const { data } = useSelectedTeamAndRepo()
  const [connections, setConnections] =
    React.useState<IntegrationInstructionsItem[]>()
  const [agentKey, setAgentKey] = React.useState<string | undefined>()
  const [embedUrl, setEmbedUrl] = React.useState<string | undefined>()
  const [chatUrl, setChatUrl] = React.useState<string | undefined>()
  const [mcpUrl, setMcpUrl] = React.useState<string | undefined>()
  const [slackInstallUrl, setSlackInstallUrl] = React.useState<
    string | undefined
  >()
  const { data: branchOrCommit } = useSelectedBranchOrCommitQuery()

  const hasAgentData = Boolean(embedUrl)

  React.useEffect(() => {
    if (isVisitor) return

    run(async () => {
      const {
        baseHubAgents: { integrationInstructions },
      } = await basehub({
        token: env.NEXT_PUBLIC_BASEHUB_TOKEN,
      }).query({
        baseHubAgents: {
          integrationInstructions: {
            items: {
              _title: true,
              _id: true,
              body: {
                json: {
                  content: true,
                  blocks: {
                    __typename: true,
                    on_ChatSnippetComponent: {
                      _id: true,
                    },
                    on_EmbedSnippetComponent: {
                      _id: true,
                    },
                    on_McpSnippetComponent: {
                      _id: true,
                    },
                    on_McpAddToCursorButtonComponent: {
                      _id: true,
                    },
                    on_SlackCommandSnippetComponent: {
                      _id: true,
                    },
                    on_SlackInstallUrlComponent: {
                      _id: true,
                    },
                  },
                },
              },
              icon: true,
            },
          },
        },
      })

      setConnections(
        integrationInstructions.items as unknown as IntegrationInstructionsItem[]
      )
    })
  }, [isVisitor])

  const fetchAgentData = React.useCallback(async () => {
    if (isVisitor) return
    if (!branchOrCommit) return

    const ref = isBranch(branchOrCommit)
      ? branchOrCommit.name
      : branchOrCommit.id
    if (!ref) return

    const repository = data?.repo
    if (!repository) return

    const readToken = isUserRepo(repository)
      ? repository.tokens.find((t) => t.scope === 'read')?.token
      : null

    if (!readToken) return

    try {
      const json = await fetch(
        getURL('api', process.env.NEXT_PUBLIC_ENVIRONMENT as Environment) +
          '/graphql',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-basehub-token': readToken,
            'x-basehub-ref': ref,
            'x-basehub-draft': 'true',
          },
          body: JSON.stringify({
            query: `
              query GetAgent($id: String!) {
                _agent(id: $id) {
                  _agentKey
                  chatUrl
                  embedUrl
                  mcpUrl
                  slackInstallUrl
                }
              }
            `,
            variables: { id: idToSourceBlockId(block) },
          }),
        }
      ).then((res) => res.json())

      const parsed = z
        .object({
          data: z.object({
            _agent: z
              .object({
                _agentKey: z.string(),
                embedUrl: z.string(),
                chatUrl: z.string(),
                mcpUrl: z.string(),
                slackInstallUrl: z.string(),
              })
              .nullable(),
          }),
        })
        .safeParse(json)

      if (parsed.success && parsed.data.data._agent) {
        const agent = parsed.data.data._agent
        setAgentKey(agent._agentKey)
        setEmbedUrl(agent.embedUrl)
        setChatUrl(agent.chatUrl)
        setMcpUrl(agent.mcpUrl)
        setSlackInstallUrl(agent.slackInstallUrl)
      } else {
        setAgentKey(undefined)
        setEmbedUrl(undefined)
        setChatUrl(undefined)
        setMcpUrl(undefined)
        setSlackInstallUrl(undefined)
      }
    } catch (error) {
      console.error(error)
    }
  }, [block, branchOrCommit, data?.repo, isVisitor])

  // Fetch on mount and when deps change
  React.useEffect(() => {
    fetchAgentData()
  }, [fetchAgentData])

  // Poll every 5s until we have agent data
  React.useEffect(() => {
    if (hasAgentData) return
    const interval = setInterval(fetchAgentData, 5000)
    return () => clearInterval(interval)
  }, [hasAgentData, fetchAgentData])

  if (isVisitor) return null
  if (!connections?.length) return null

  return (
    <Dialog.Dialog open={isOn} onOpenChange={handleToggle}>
      <SimpleTooltip
        content="Commit the agent to enable connections."
        disabled={hasAgentData}
      >
        <Dialog.DialogTrigger asChild disabled={!hasAgentData}>
          <Button
            unstyled
            focusStyled
            className={clsx(
              $breadcrumbCta(),
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-dark-muted'
            )}
            disabled={!hasAgentData}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.997 3.62821C12.9564 3.58753 12.9081 3.55527 12.855 3.53325C12.8019 3.51123 12.745 3.4999 12.6875 3.4999C12.63 3.4999 12.5731 3.51123 12.52 3.53325C12.4669 3.55527 12.4186 3.58753 12.378 3.62821L10.5 5.50673L8.49352 3.50024L10.372 1.62227C10.4541 1.54018 10.5002 1.42884 10.5002 1.31274C10.5002 1.19665 10.4541 1.0853 10.372 1.00321C10.2899 0.921119 10.1786 0.875 10.0625 0.875C9.9464 0.875 9.83506 0.921119 9.75297 1.00321L7.875 2.88173L6.43453 1.44071C6.35244 1.35862 6.2411 1.3125 6.125 1.3125C6.0089 1.3125 5.89756 1.35862 5.81547 1.44071C5.73337 1.5228 5.68726 1.63415 5.68726 1.75024C5.68726 1.86634 5.73337 1.97768 5.81547 2.05977L6.16273 2.40649L3.2643 5.30493C3.06116 5.50806 2.90002 5.74921 2.79008 6.01462C2.68014 6.28003 2.62355 6.56449 2.62355 6.85177C2.62355 7.13904 2.68014 7.42351 2.79008 7.68891C2.90002 7.95432 3.06116 8.19547 3.2643 8.3986L4.12344 9.25774L1.44047 11.9407C1.39982 11.9814 1.36758 12.0296 1.34558 12.0827C1.32358 12.1358 1.31226 12.1928 1.31226 12.2502C1.31226 12.3077 1.32358 12.3647 1.34558 12.4178C1.36758 12.4709 1.39982 12.5191 1.44047 12.5598C1.52256 12.6419 1.6339 12.688 1.75 12.688C1.80748 12.688 1.86441 12.6767 1.91752 12.6547C1.97063 12.6327 2.01888 12.6004 2.05953 12.5598L4.74414 9.87517L5.60328 10.7343C5.80641 10.9374 6.04756 11.0986 6.31297 11.2085C6.57838 11.3185 6.86284 11.3751 7.15012 11.3751C7.43739 11.3751 7.72186 11.3185 7.98726 11.2085C8.25267 11.0986 8.49382 10.9374 8.69695 10.7343L11.5954 7.83587L11.9421 8.18313C11.9828 8.22378 12.031 8.25603 12.0841 8.27802C12.1372 8.30002 12.1942 8.31135 12.2516 8.31135C12.3091 8.31135 12.366 8.30002 12.4192 8.27802C12.4723 8.25603 12.5205 8.22378 12.5612 8.18313C12.6018 8.14249 12.6341 8.09423 12.6561 8.04112C12.6781 7.98801 12.6894 7.93109 12.6894 7.8736C12.6894 7.81612 12.6781 7.75919 12.6561 7.70609C12.6341 7.65298 12.6018 7.60472 12.5612 7.56407L11.1185 6.12524L12.997 4.24727C13.0377 4.20664 13.07 4.15839 13.092 4.10528C13.114 4.05217 13.1253 3.99524 13.1253 3.93774C13.1253 3.88025 13.114 3.82332 13.092 3.77021C13.07 3.7171 13.0377 3.66884 12.997 3.62821ZM8.07844 10.1174C7.95655 10.2394 7.81183 10.3361 7.65255 10.4021C7.49326 10.4681 7.32253 10.5021 7.15012 10.5021C6.9777 10.5021 6.80697 10.4681 6.64769 10.4021C6.4884 10.3361 6.34368 10.2394 6.2218 10.1174L3.88281 7.77845C3.76086 7.65656 3.66412 7.51184 3.59812 7.35256C3.53212 7.19327 3.49815 7.02254 3.49815 6.85013C3.49815 6.67771 3.53212 6.50698 3.59812 6.3477C3.66412 6.18841 3.76086 6.04369 3.88281 5.92181L6.78125 3.02337L10.9752 7.21899L8.07844 10.1174Z"
                fill="currentColor"
              />
            </svg>
            <span>Connect</span>
          </Button>
        </Dialog.DialogTrigger>
      </SimpleTooltip>
      <Dialog.DialogContent
        overlayProps={{ onClick: handleOff }}
        className="w-full !max-w-screen-sm !pb-6 !pt-4 [&>hr:last-of-type]:mt-0 !top-[100px] !translate-y-0 max-h-max"
      >
        <Dialog.DialogHeader divider className="!-mb-1">
          <Dialog.DialogTitle>
            Connect to {block.title || 'your Agent'}
          </Dialog.DialogTitle>
        </Dialog.DialogHeader>

        <Tabs>
          <TabsList
            className="flex items-center gap-x-2 overflow-x-auto !p-0 -ml-px"
            defaultValue={connections?.[0]?._id}
          >
            {connections?.map((connection) => (
              <TabsTrigger
                value={connection._id}
                key={connection._id}
                className="group !text-dark-bright focus-visible:outline-none focus:outline-none focus-visible:ring-0 text-sm group relative cursor-default !min-w-0 radix-state-inactive:hover:!text-opacity-70 radix-state-inactive:!text-opacity-30 font-medium"
              >
                <div className="group-focus-visible:outline group-focus-visible:outline-dark-control-base group-focus-visible:outline-1 rounded-md absolute inset-1 group-active:outline-none" />

                <Icon
                  content={connection.icon}
                  components={{
                    svg: (props) => <svg {...props} className="size-4 mr-1" />,
                  }}
                />
                {connection._title}
              </TabsTrigger>
            ))}
          </TabsList>

          <hr className="border-dark-border-solid -mt-px mb-6 text-sm text-dark-muted" />

          {connections?.map((connection) => {
            return (
              <TabsContent
                key={connection._id}
                value={connection._id}
                className="mt-5"
              >
                <div className="flex flex-col gap-y-3">
                  <RichText
                    content={connection.body?.json.content}
                    blocks={connection.body.json.blocks}
                    components={{
                      p: (props) => (
                        <p className="text-sm text-dark-muted" {...props} />
                      ),
                      ol: (props) => (
                        <ol
                          className="list-decimal ml-4 space-y-1"
                          {...props}
                        />
                      ),
                      ul: (props) => (
                        <ul className="list-disc ml-4 space-y-1" {...props} />
                      ),
                      li: (props) => (
                        <li
                          className="text-sm text-dark-muted [&>p]:inline-block space-y-2"
                          {...props}
                        />
                      ),
                      ChatSnippetComponent: () => {
                        if (!chatUrl) return 'Loading...'
                        return (
                          <ButtonLink
                            unstyled
                            focusStyled
                            href={chatUrl}
                            target="_blank"
                            className="text-dark-muted hover:text-dark-bright overflow-hidden w-fit text-xs flex items-center gap-x-1 rounded border border-dark-border-solid px-2 py-1 bg-dark-shade hover:bg-dark-shade-hover hover:border-dark-elevated"
                          >
                            <ExternalLinkIcon />
                            <span>Chat with {block.title || 'your Agent'}</span>
                          </ButtonLink>
                        )
                      },
                      EmbedSnippetComponent: () => {
                        return (
                          <CodeWithVariables
                            block={block}
                            code={`<script
  src="${embedUrl ?? 'Loading...'}"
>
</script>`}
                            language="bash"
                          />
                        )
                      },
                      McpSnippetComponent: () => {
                        return (
                          <CodeWithVariables
                            block={block}
                            code={JSON.stringify(
                              { url: mcpUrl ?? 'Loading...' },
                              null,
                              2
                            )}
                            language="json"
                          />
                        )
                      },
                      McpAddToCursorButtonComponent: () => {
                        if (!mcpUrl) return 'Loading...'

                        const mcpConfig = { url: mcpUrl }

                        const encodedConfig = Buffer.from(
                          JSON.stringify(mcpConfig)
                        ).toString('base64')

                        const mcpName = slugify(block.title)
                        const cursorInstallUrl = `cursor://anysphere.cursor-deeplink/mcp/install?name=${mcpName}&config=${encodedConfig}`
                        return (
                          <ButtonLink
                            unstyled
                            focusStyled
                            href={cursorInstallUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="block h-8 w-[126px] overflow-hidden -mt-2"
                          >
                            <BaseHubImage
                              className="block dark:hidden h-8 w-[126px] object-contain"
                              src="https://cursor.com/deeplink/mcp-install-dark.svg"
                              alt="Add basehub MCP server to Cursor"
                              height="32"
                              width="126"
                            />
                            <BaseHubImage
                              className="hidden dark:block h-8 w-[126px] object-contain"
                              src="https://cursor.com/deeplink/mcp-install-light.svg"
                              alt="Add basehub MCP server to Cursor"
                              height="32"
                              width="126"
                            />
                          </ButtonLink>
                        )
                      },
                      SlackInstallUrlComponent_mark: ({ children }) => {
                        return (
                          <a
                            href={slackInstallUrl ?? ''}
                            target="_blank"
                            rel="noreferrer"
                            className="text-dark-accent hover:underline"
                          >
                            {children}
                          </a>
                        )
                      },
                      SlackCommandSnippetComponent: () => {
                        if (!agentKey) return 'Loading...'
                        return (
                          <CodeWithVariables
                            block={block}
                            code={`/basehub agent add ${agentKey}`}
                            language="bash"
                          />
                        )
                      },
                    }}
                  />
                </div>
              </TabsContent>
            )
          })}
        </Tabs>
      </Dialog.DialogContent>
    </Dialog.Dialog>
  )
}

const CodeWithVariables = ({
  code,
  ...props
}: CodeSnippetProps & React.PropsWithChildren<{ block: BreadcrumbBlock }>) => {
  return (
    <CodeSnippet
      className="light:bg-dark-base-dark !p-3.5"
      theme={terminalTheme}
      {...props}
      code={code}
    />
  )
}
